import { api } from "../api/client"
import { CurrentCall, useCallStore } from "../../stores/call-store"

const TWILIO_CALL_SID_REGEX = /^CA[0-9a-f]{32}$/i

type AgentCallStateData = NonNullable<
  Awaited<ReturnType<typeof api.callCenter.agentCallState>>["data"]["data"]
>

let currentCallSyncPromise: Promise<CurrentCall | null> | null = null

export function isTwilioCallSid(value?: string | null): value is string {
  return Boolean(value && TWILIO_CALL_SID_REGEX.test(value))
}

function toCurrentCall(callData: AgentCallStateData): CurrentCall {
  return {
    call_id: callData.call_id,
    name: callData.name || "",
    number: callData.number || "",
    call_sid: isTwilioCallSid(callData.call_sid) ? callData.call_sid : "",
    contact: callData.contact || undefined,
    matter: callData.matter || undefined,
  }
}

export async function hydrateCurrentCallFromServer(
  reason: string,
  options: { clearIfMissing?: boolean; hasLiveTwilioCall?: boolean } = {}
): Promise<CurrentCall | null> {
  const { clearIfMissing = true, hasLiveTwilioCall = false } = options

  if (currentCallSyncPromise) {
    return currentCallSyncPromise
  }

  currentCallSyncPromise = (async () => {
    try {
      const result = await api.callCenter.agentCallState()
      const callData = result?.data?.data

      if (callData) {
        const currentCall = toCurrentCall(callData)
        useCallStore.getState().hydrateCurrentCall(currentCall)
        console.warn(`[call-sync] Hydrated currentCall from server (${reason})`)
        return currentCall
      }

      if (clearIfMissing && !hasLiveTwilioCall && useCallStore.getState().currentCall) {
        console.warn(`[call-sync] Clearing stale currentCall because server returned no active call (${reason})`)
        useCallStore.getState().resetCallState()
      }

      return null
    } catch (error) {
      console.error(`[call-sync] Failed to hydrate currentCall from server (${reason})`, error)
      return useCallStore.getState().currentCall
    } finally {
      currentCallSyncPromise = null
    }
  })()

  return currentCallSyncPromise
}
